import { useState, useCallback, useRef, useEffect } from 'react'
import { Flex, TextInput, Text } from '@applyboard/crystal-ui'
import styled from '@emotion/styled'
import { useGooglePlacesAutocomplete } from '../../hooks/useGooglePlacesAutocomplete'
import { AddressAutocompleteProps, AutocompleteSuggestion } from './types'

/**
 * Address autocomplete component using Google Places API
 * Provides address suggestions as user types and parses selected address into structured fields
 */
export function AddressAutocomplete(props: AddressAutocompleteProps) {
  const {
    label,
    id = 'address-autocomplete',
    value = '',
    disabled = false,
    required,
    helpText,
    onAddressSelect,
    onChange,
    apiKey,
    errorMessage,
    countryIsoCode,
    onError,
  } = props

  const [inputValue, setInputValue] = useState(value)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const {
    suggestions,
    isLoading,
    searchAddress,
    selectPlace,
    clearSuggestions,
    startSession,
    endSession,
  } = useGooglePlacesAutocomplete({
    apiKey,
    countryCode: countryIsoCode,
    onError: error => {
      onError?.(error)
    },
  })

  const handleInputChange = useCallback(
    (newValue: string | number) => {
      const stringValue = String(newValue)
      setInputValue(stringValue)
      setSelectedIndex(-1)
      onChange?.(stringValue)

      if (apiKey && stringValue.length >= 3) {
        searchAddress(stringValue)
        setShowSuggestions(true)
      } else {
        clearSuggestions()
        setShowSuggestions(false)
      }
    },
    [apiKey, searchAddress, clearSuggestions, onChange],
  )

  const handleSuggestionSelect = useCallback(
    async (suggestion: AutocompleteSuggestion) => {
      setInputValue(suggestion.mainText)
      setShowSuggestions(false)
      clearSuggestions()

      const address = await selectPlace(suggestion.placeId, suggestion.mainText)
      if (address) {
        onAddressSelect(address)
      }
    },
    [selectPlace, clearSuggestions, onAddressSelect],
  )

  const handleFocus = useCallback(() => {
    startSession()
    if (suggestions.length > 0) {
      setShowSuggestions(true)
    }
  }, [startSession, suggestions.length])

  const handleBlur = useCallback(() => {
    // Delay hiding suggestions to allow click to register
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false)
        endSession()
      }
    }, 200)
  }, [endSession])

  // Crystal UI TextInput onKeyDown passes key string, not KeyboardEvent
  const handleKeyDown = useCallback(
    (key: string) => {
      if (!showSuggestions || suggestions.length === 0) return

      switch (key) {
        case 'ArrowDown':
          setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1))
          break
        case 'Enter':
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            handleSuggestionSelect(suggestions[selectedIndex])
          }
          break
        case 'Escape':
          setShowSuggestions(false)
          clearSuggestions()
          break
      }
    },
    [showSuggestions, suggestions, selectedIndex, handleSuggestionSelect, clearSuggestions],
  )

  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const handleNativeKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        showSuggestions &&
        selectedIndex >= 0 &&
        selectedIndex < suggestions.length
      ) {
        event.preventDefault()
      }
    }

    input.addEventListener('keydown', handleNativeKeyDown)
    return () => input.removeEventListener('keydown', handleNativeKeyDown)
  }, [showSuggestions, selectedIndex, suggestions.length])

  // Convert string required to boolean for Crystal UI TextInput
  const isRequired = typeof required === 'string' ? true : required

  // If no API key, render a simple text input (graceful fallback)
  if (!apiKey) {
    return (
      <TextInput
        label={label}
        value={inputValue}
        disabled={disabled}
        required={isRequired}
        helpText={errorMessage || helpText}
        intent={errorMessage ? 'negative' : undefined}
        onChange={handleInputChange}
      />
    )
  }

  return (
    <Container ref={containerRef}>
      <TextInput
        ref={inputRef}
        label={label}
        value={inputValue}
        disabled={disabled}
        required={isRequired}
        helpText={errorMessage || helpText || 'Start typing to see address suggestions'}
        intent={errorMessage ? 'negative' : undefined}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-controls={`${id}-suggestions`}
        aria-expanded={showSuggestions && suggestions.length > 0}
        aria-activedescendant={
          showSuggestions && selectedIndex >= 0 ? `${id}-suggestion-${selectedIndex}` : undefined
        }
      />

      {showSuggestions && suggestions.length > 0 && (
        <SuggestionsContainer>
          <SuggestionsList id={`${id}-suggestions`} role="listbox">
            {suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={suggestion.placeId}
                id={`${id}-suggestion-${index}`}
                role="option"
                aria-selected={index === selectedIndex}
                isSelected={index === selectedIndex}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <Flex direction="column" gap={1}>
                  <Text variant="bodyM">{suggestion.mainText}</Text>
                  <Text variant="bodyS" contrast="mid">
                    {suggestion.secondaryText}
                  </Text>
                </Flex>
              </SuggestionItem>
            ))}
          </SuggestionsList>
        </SuggestionsContainer>
      )}

      {isLoading && showSuggestions && (
        <SuggestionsContainer>
          <Flex justify="center" p={4}>
            <Text variant="bodyS" contrast="mid">
              Loading suggestions...
            </Text>
          </Flex>
        </SuggestionsContainer>
      )}
    </Container>
  )
}

const Container = styled.div({
  position: 'relative',
  width: '100%',
})

const SuggestionsContainer = styled.div(({ theme }) => ({
  position: 'absolute',
  top: 'calc(100% - 30px)',
  left: 0,
  right: 0,
  zIndex: 1000,
  maxHeight: 300,
  overflowY: 'auto' as const,
  backgroundColor: theme.colors.bgRegular,
  borderRadius: '0 0 8px 8px',
  border: `1px solid ${theme.colors.borderRegular}`,
  borderTop: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
}))

const SuggestionsList = styled.ul({
  listStyle: 'none',
  margin: 0,
  padding: 0,
})

const SuggestionItem = styled.li<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: `1px solid ${theme.colors.borderInactive}`,
  transition: 'background-color 0.15s ease',
  backgroundColor: isSelected ? theme.colors.bgSubtle : undefined,
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: theme.colors.bgSubtle,
  },
  '&:focus': {
    outline: `2px solid ${theme.colors.actionPrimary}`,
    outlineOffset: -2,
  },
}))
